/**
 * Estimate SMSWiser drop volume (drops/day) from a newest-first sample batch.
 *
 * Probe with {@link DROPS_CALIBRATION_SAMPLE_SIZE} rows, count unique calendar
 * days in that sample, then: dropsPerDay = sampleSize / uniqueDays.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { getDropDateYmd, type SmswiserDropDateLike } from './drops-date-filter';

/** Rows fetched to measure how many calendar days 5k drops spans. */
export const DROPS_CALIBRATION_SAMPLE_SIZE = 5000;

/** Used before a probe sample exists (UI preview / empty batch). */
export const DROPS_PER_DAY_FALLBACK = 150;

/** Count distinct YYYY-MM-DD values present in the sample. */
export function countUniqueDropDays(drops: SmswiserDropDateLike[]): number {
  const days = new Set<string>();
  for (const drop of drops) {
    const ymd = getDropDateYmd(drop);
    if (ymd) days.add(ymd);
  }
  return days.size;
}

/**
 * dropsPerDay ≈ sample.length / uniqueDaysInSample.
 * Falls back to {@link DROPS_PER_DAY_FALLBACK} when the sample is empty.
 */
export function estimateDropsPerDayFromSample(
  drops: SmswiserDropDateLike[],
): number {
  if (drops.length === 0) return DROPS_PER_DAY_FALLBACK;
  const uniqueDays = Math.max(1, countUniqueDropDays(drops));
  return Math.ceil(drops.length / uniqueDays);
}
